<script lang="ts">
  import { onMount } from 'svelte';
  import { getTransactions } from '../lib/storage.js';
  import {
    createCategorizationPipeline,
    processUploadedFiles,
    type ImportAnalysis
  } from '../lib/uploadService';
  import { parseFilename, shouldPromptForMetadata } from '../lib/utils/filenameParser';
  import UploadDropzone from './upload/UploadDropzone.svelte';
  import UploadMessage from './upload/UploadMessage.svelte';
  import MetadataPromptModal from './upload/MetadataPromptModal.svelte';
  import CategorizationReviewModal from './categorization/CategorizationReviewModal.svelte';

  let {
    onUpload = () => {}
  }: {
    onUpload?: () => void;
  } = $props();

  let uploading = $state<boolean>(false);
  let message = $state<string>('');
  let messageType = $state<'success' | 'error' | 'info' | 'warning'>('info');
  let categorizationPipeline = $state<any>(null);

  // Metadata prompt modal state
  let showMetadataPrompt = $state<boolean>(false);
  let currentFilename = $state<string>('');
  let suggestedBank = $state<string>('');
  let suggestedPerson = $state<string>('');
  let metadataResolve: ((value: { bank: string; person: string }) => void) | null = null;

  // Categorization review modal state
  let showCategorizationReview = $state<boolean>(false);
  let reviewTransactions = $state<any[]>([]);

  // File metadata cache
  type FileMetadata = { bank?: string; person?: string; accountType?: string; accountName?: string };
  let fileMetadataMap = new Map<string, FileMetadata>();

  onMount(async () => {
    const existingTransactions = await getTransactions();
    console.log('Existing transactions in storage:', existingTransactions.length);

    // Initialize categorization pipeline
    categorizationPipeline = createCategorizationPipeline();
    console.log('Categorization pipeline initialized');
  });

  /**
   * Collect metadata for a file (parse or prompt user)
   */
  async function getFileMetadata(file: File): Promise<FileMetadata> {
    // Parse filename
    const parsed = parseFilename(file.name);
    console.log(`Parsed ${file.name}:`, parsed);

    // If confidence is high, use parsed metadata
    if (!shouldPromptForMetadata(parsed)) {
      return {
        bank: parsed.bank,
        person: parsed.person
      };
    }

    // Otherwise, prompt user
    return new Promise<FileMetadata>((resolve) => {
      currentFilename = file.name;
      suggestedBank = parsed.bank || '';
      suggestedPerson = parsed.person || '';
      metadataResolve = resolve;
      showMetadataPrompt = true;
    });
  }

  function handleMetadataConfirm(bank: string, person: string, accountType: string, accountName: string) {
    if (metadataResolve) {
      // Return all metadata fields separately
      // The person field for transactions will be built from these values
      // but we also store them separately in the processed_files table
      metadataResolve({ bank, person, accountType, accountName });
      metadataResolve = null;
    }
  }

  function handleMetadataCancel() {
    if (metadataResolve) {
      metadataResolve({ bank: undefined, person: undefined });
      metadataResolve = null;
    }
  }

  async function handleFileSelect(files: FileList): Promise<void> {
    console.log('=== handleFileSelect START ===');
    console.log('Files received:', files.length);

    // CRITICAL: Convert FileList to array IMMEDIATELY before any async operations
    // FileList can become invalid after awaits!
    const filesArray = Array.from(files);
    console.log('Files array (converted immediately):', filesArray.map(f => f.name));

    uploading = true;
    message = 'Uploading files...';
    messageType = 'info';
    fileMetadataMap.clear();

    try {
      console.log('Importing file processing utilities...');
      // Import file processing utilities
      const { isFileHashProcessed } = await import('../lib/storage');
      const { hashFile } = await import('../lib/utils/fileHash');
      console.log('Utilities imported successfully');

      // Step 1: Hash files and check for duplicates (by content, not filename)
      const unprocessedFiles: File[] = [];
      let alreadyProcessedCount = 0;
      const fileHashes = new Map<string, string>(); // filename -> hash

      console.log('Starting file hash check...');
      for (const file of filesArray) {
        console.log(`Hashing ${file.name}...`);
        const fileHash = await hashFile(file);
        fileHashes.set(file.name, fileHash);
        console.log(`${file.name} hash: ${fileHash.slice(0, 8)}...`);

        // Check if this file content (hash) has been processed before
        const hashCheck = await isFileHashProcessed(fileHash);

        if (hashCheck.processed) {
          console.log(`[FileUpload] 📄 FILE HASH DUPLICATE: "${file.name}" has same content as "${hashCheck.filename}"`, {
            fileHash: fileHash.slice(0, 12) + '...',
            originalFile: hashCheck.filename,
            duplicateFile: file.name
          });
          alreadyProcessedCount++;
        } else {
          console.log(`[FileUpload] ✨ NEW FILE: "${file.name}" (hash: ${fileHash.slice(0, 12)}...)`);
          unprocessedFiles.push(file);
        }
      }

      console.log(`Unprocessed files: ${unprocessedFiles.length}, Already processed: ${alreadyProcessedCount}`);

      // If all files are already processed, show message and exit
      if (unprocessedFiles.length === 0) {
        message = `All ${alreadyProcessedCount} file(s) already processed`;
        messageType = 'info';
        uploading = false;

        setTimeout(() => {
          if (messageType === 'info') {
            message = '';
          }
        }, 3000);
        return;
      }

      // Step 2: Collect metadata for unprocessed files only
      for (const file of unprocessedFiles) {
        const metadata = await getFileMetadata(file);
        fileMetadataMap.set(file.name, metadata);
        console.log(`Metadata for ${file.name}:`, metadata);
      }

      // Step 3: Process files with metadata and hashes using enhanced upload service
      const result = await processUploadedFilesWithMetadata(unprocessedFiles, categorizationPipeline, fileMetadataMap, fileHashes);

      // Add already processed count to the message if any
      if (alreadyProcessedCount > 0 && result.success) {
        result.message += ` • ${alreadyProcessedCount} file(s) already processed`;
      }

      message = result.message;

      // Determine message type based on result
      if (result.success) {
        messageType = 'success';

        // Check if there are medium-confidence transactions to review
        if (result.analysis?.mediumConfidenceTransactions && result.analysis.mediumConfidenceTransactions.length > 0) {
          reviewTransactions = result.analysis.mediumConfidenceTransactions;
          showCategorizationReview = true;
        } else {
          onUpload();
        }

        // Clear success message after 3 seconds
        setTimeout(() => {
          if (messageType === 'success') {
            message = '';
          }
        }, 3000);
      } else if (result.skippedCount > 0 && result.processedCount === 0) {
        // All files already processed - warning, not error
        messageType = 'info';

        // Clear info message after 3 seconds
        setTimeout(() => {
          if (messageType === 'info') {
            message = '';
          }
        }, 3000);
      } else {
        messageType = 'error';
      }
    } catch (error) {
      console.error('Error processing files:', error);
      message = `Error: ${error.message}`;
      messageType = 'error';
    } finally {
      uploading = false;
      fileMetadataMap.clear();
    }
  }

  /**
   * Enhanced version of processUploadedFiles that applies metadata and file hashes
   */
  async function processUploadedFilesWithMetadata(
    files: File[],
    pipeline: any,
    metadataMap: Map<string, FileMetadata>,
    hashMap: Map<string, string>
  ) {
    console.log('=== processUploadedFilesWithMetadata START ===');
    console.log('Files to process:', files.length);
    console.log('Metadata map:', metadataMap);
    console.log('Hash map size:', hashMap.size);
    console.log('Pipeline:', pipeline);

    const { parseCSV } = await import('../lib/parsers/genericParser');
    const { applyCategorizationToTransactions } = await import('../lib/uploadService');
    const {
      saveTransactionsForFile,
      markFileAsProcessed,
      isFileProcessed
    } = await import('../lib/storage');

    let allNewTransactions: any[] = [];
    let processedCount = 0;
    let skippedCount = 0;
    let totalDuplicates = 0;

    console.log('Starting file processing loop...');

    for (const file of files) {
      // Check if already processed (should be redundant now, but kept for safety)
      if (await isFileProcessed(file.name)) {
        skippedCount++;
        continue;
      }

      // Get metadata and hash for this file
      const metadata = metadataMap.get(file.name) || {};
      const fileHash = hashMap.get(file.name);

      // Parse CSV
      const text = await file.text();
      let transactions = parseCSV(text, file.name, metadata.bank);
      console.log(`Parsed ${transactions.length} transactions from ${file.name}`);

      // Apply metadata to all transactions
      // Build person field from person, accountType, and accountName
      let personValue = metadata.person;
      if (!personValue && metadata.accountName) {
        personValue = metadata.accountName; // e.g., "Blue Cash"
      } else if (!personValue && metadata.accountType) {
        personValue = metadata.accountType; // e.g., "Credit Card"
      } else if (personValue && (metadata.accountType || metadata.accountName)) {
        // Combine: e.g., "Shelly - Credit Card" or "Shelly - Blue Cash"
        const suffix = metadata.accountName || metadata.accountType;
        personValue = `${personValue} - ${suffix}`;
      }

      transactions = transactions.map((t: any) => ({
        ...t,
        bank: t.bank || metadata.bank, // Use detected bank, fallback to metadata
        person: t.person || personValue
      }));
      console.log(`Applied metadata - bank: ${metadata.bank}, person: ${personValue} (from: ${metadata.person}, ${metadata.accountType}, ${metadata.accountName})`);

      // Apply categorization
      transactions = await applyCategorizationToTransactions(transactions, pipeline);
      console.log(`Categorization applied to ${transactions.length} transactions`);

      // Save transactions
      if (transactions.length > 0) {
        console.log(`Attempting to save ${transactions.length} transactions from ${file.name}`);
        const saveResult = await saveTransactionsForFile(file.name, transactions);
        console.log(`Save result for ${file.name}:`, saveResult);

        if (saveResult) {
          const duplicatesSkipped = typeof saveResult === 'object' ? saveResult.duplicatesSkipped || 0 : 0;
          const savedCount = typeof saveResult === 'object' ? saveResult.saved || 0 : transactions.length;

          console.log(`${file.name}: saved ${savedCount}, duplicates ${duplicatesSkipped}`);

          // Always track duplicates, even if nothing was saved
          totalDuplicates += duplicatesSkipped;

          if (savedCount > 0) {
            // Mark file as processed with hash and metadata
            await markFileAsProcessed(file.name, savedCount, fileHash, metadata);
            allNewTransactions = [...allNewTransactions, ...transactions.slice(0, savedCount)];
            processedCount++;
          }
        } else {
          console.warn(`saveTransactionsForFile returned falsy value for ${file.name}`);
        }
      } else {
        console.warn(`No transactions to save from ${file.name}`);
      }
    }

    // Build result message
    let success = allNewTransactions.length > 0;
    let resultMessage = '';

    if (success) {
      resultMessage = `✅ Imported ${allNewTransactions.length} transactions from ${processedCount} file(s)`;
      if (totalDuplicates > 0) {
        resultMessage += ` (${totalDuplicates} duplicates skipped)`;
      }
      if (skippedCount > 0) {
        resultMessage += ` • ${skippedCount} file(s) already processed`;
      }
    } else if (skippedCount > 0) {
      resultMessage = `All ${skippedCount} file(s) already processed`;
    } else if (totalDuplicates > 0) {
      resultMessage = `All ${totalDuplicates} transaction(s) from ${files.length} file(s) are duplicates (already imported)`;
    } else {
      resultMessage = 'No valid transactions found in files';
    }

    return {
      success,
      newTransactions: allNewTransactions,
      processedCount,
      skippedCount,
      failedCount: 0,
      message: resultMessage,
      analysis: undefined // We'll add analysis later if needed
    };
  }

  function handleReviewClose() {
    showCategorizationReview = false;
    reviewTransactions = [];
    onUpload();
  }

  async function handleReviewComplete(event: CustomEvent) {
    const decisions = event.detail;
    console.log('Review complete, decisions:', decisions);

    try {
      // Filter decisions where user wants to create a rule
      const ruleDecisions = decisions.filter((d: any) => d.createRule && d.action !== 'skip');

      if (ruleDecisions.length > 0) {
        // Import the API function
        const { createRulesFromReview } = await import('../lib/api');

        // Create rules from decisions
        const rulesToCreate = ruleDecisions.map((d: any) => ({
          description: d.description,
          category: d.confirmedCategory,
          person: null
        }));

        const result = await createRulesFromReview(rulesToCreate);
        console.log(`Created ${result.created} rules from review`);

        // Apply the new rules to existing transactions
        // This will update similar transactions that match the new patterns
        message = `Created ${result.created} new categorization rule(s)!`;
        messageType = 'success';

        // Clear message after 3 seconds
        setTimeout(() => {
          if (messageType === 'success') {
            message = '';
          }
        }, 3000);
      }

      showCategorizationReview = false;
      reviewTransactions = [];
      onUpload();
    } catch (error) {
      console.error('Error creating rules from review:', error);
      message = `Error creating rules: ${error.message}`;
      messageType = 'error';
    }
  }
</script>

<div class="file-upload">
  <UploadDropzone onFileSelect={handleFileSelect} uploading={uploading} />
  {#if message}
    <UploadMessage {message} type={messageType} />
  {/if}
</div>

<!-- Metadata Prompt Modal -->
<MetadataPromptModal
  bind:isOpen={showMetadataPrompt}
  filename={currentFilename}
  suggestedBank={suggestedBank}
  suggestedPerson={suggestedPerson}
  onConfirm={handleMetadataConfirm}
  onCancel={handleMetadataCancel}
/>

<!-- Categorization Review Modal -->
<CategorizationReviewModal
  isOpen={showCategorizationReview}
  transactions={reviewTransactions}
  onClose={handleReviewClose}
  onComplete={(decisions) => handleReviewComplete(new CustomEvent('complete', { detail: decisions }))}
/>

<style lang="scss">
  .file-upload {
    max-width: var(--container-max-width);
    margin: 0 auto var(--spacing-xl) auto;
  }
</style>
